/**
 * A ligação entre o estado da sessão e a tela de entrada.
 *
 * `Entrada` é a tela, com nove situações e nenhum estado próprio. Quem sabe em
 * que situação a pessoa está é a sessão — e é aqui que uma coisa vira a outra.
 * A tabela é uma função pura, `situacaoDe`, para o mapeamento ser provado sem
 * montar nada.
 *
 * ── O que a sessão expirada NÃO faz aqui ─────────────────────────────────
 *
 * `expirado` continua dentro da casca, como faixa: a aplicação fica montada e
 * o rascunho, na tela — e reentrar por popup devolve a pessoa exatamente onde
 * estava. A tela 6 do desenho é para quando isso não é possível; hoje ela não
 * tem onde aparecer, e fica desenhada em `Entrada` para o dia em que tiver.
 */

import { useState, type ReactNode } from 'react';
import { useSessao } from '@/shared/sessao/contexto';
import { Entrada, situacaoDe, SUPORTE } from '@/widgets/entrada';

export function Login(): ReactNode {
  const { estado, entrar, escolherRevenda, sair, recomecar } = useSessao();
  const [indo, definirIndo] = useState(false);
  /*
   * Qual organização está entrando.
   *
   * Sem isto, dois cliques começam dois fluxos OIDC — e o segundo sobrescreve
   * o pedido guardado do primeiro no `sessionStorage`. A volta traz um `state`
   * que não confere, e a pessoa vê "o login está fora do ar" por ter clicado
   * duas vezes.
   */
  const [entrando, definirEntrando] = useState<string | null>(null);

  const situacao = situacaoDe(estado);
  const revenda = estado.nome === 'erro' || estado.nome === 'saiu' ? estado.revenda : null;

  /**
   * Começar o login é UMA ação, sem parâmetro.
   *
   * Era `entrar(slug)`, depois de validar o identificador da organização —
   * porque o realm era dela e o navegador precisava saber para onde mandar o
   * login. Agora a senha é conferida num lugar só, e a organização vem
   * depois, na tela própria.
   *
   * O campo saiu junto: não há mais o que digitar aqui, e um campo que não
   * decide nada é um passo a mais antes da senha.
   */
  async function comecar(): Promise<void> {
    definirIndo(true);
    await entrar();
    definirIndo(false);
  }

  return (
    <Entrada
      situacao={situacao}
      suporte={SUPORTE}
      revenda={revenda ?? undefined}
      revendas={estado.nome === 'escolhendo' ? estado.revendas : undefined}
      entrando={entrando}
      aoEscolher={(slug) => {
        definirEntrando(slug);
        void escolherRevenda(slug);
      }}
      aoContinuar={() => {
        // `saiu` também recomeça do zero: a sessão no provedor acabou, e entrar
        // de novo é o mesmo caminho de quem chega anônimo.
        if (situacao === 'saiu') recomecar();
        else void comecar();
      }}
      indo={indo}
      pessoa={estado.nome === 'erro' ? estado.pessoa : undefined}
      correlationId={estado.nome === 'erro' ? estado.correlationId : undefined}
      detalhe={
        estado.nome === 'erro' && estado.tipo === 'provedor-fora' ? estado.motivo : undefined
      }
      /*
       * Sair também na ESCOLHA, e não só nas recusas.
       *
       * Quem entrou com a conta errada percebe ali: os nomes das organizações
       * não são os dele. Sem esta saída, o único caminho seria fechar o
       * navegador — o cookie do provedor sobreviveria, e o "entrar" seguinte
       * voltaria à mesma conta sem pedir senha.
       */
      aoSair={
        estado.nome === 'erro' || estado.nome === 'escolhendo' ? () => void sair() : undefined
      }
      aoTentarDeNovo={() => recomecar()}
    />
  );
}
